import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const teamMembers = [
  {
    name: "内容编辑部",
    role: "剧情整理与解析",
    desc: "负责剧集剧情梳理、分集解析与人物关系整理，确保内容准确、可读。",
    icon: "ri-book-open-line",
  },
  {
    name: "影评组",
    role: "点评与评分运营",
    desc: "负责观众点评的收集与整理，维护评分体系的真实与公正。",
    icon: "ri-chat-quote-line",
  },
  {
    name: "设计与技术组",
    role: "站点体验维护",
    desc: "负责站点的视觉呈现与浏览体验优化，让查找剧集信息更顺手。",
    icon: "ri-layout-masonry-line",
  },
];

export default function About() {
  useSeo({
    title: "风流女管家 - 关于我们",
    description:
      "了解风流女管家影视内容站的定位与服务范围：专注影视剧剧情介绍、演员资料与剧集点评，为观众提供可靠的剧集内容参考。",
    keywords: "风流女管家,关于我们,影视内容站,剧情介绍,剧集点评",
  });

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
          <p className="text-xs font-medium text-primary-600">关于我们</p>
          <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            让每一部好剧，都被认真看懂
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            风流女管家影视内容站，是一个专注影视剧介绍与剧情解析的内容平台。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6">
        {/* 网站介绍 */}
        <section className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              网站介绍
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-7 text-foreground-700">
              <p>
                我们以《风流女管家》为核心，围绕这部剧集提供完整的剧情介绍、分集解析、演员资料与观众点评。同时，我们也收录了一批同题材的优秀影视作品，帮助观众在有限的观剧时间里，更快找到符合口味的好剧。
              </p>
              <p>
                站内的每一篇剧情解析，都力求做到条理清晰、信息准确；演员资料则尽量完整呈现其代表作品与角色特点。我们希望这里不只是一个信息页面，更是一处可以安心“细看”影视作品的地方。
              </p>
              <p>
                目前站点的点评与收藏功能以前端演示的方式运行，数据保存在你的本地浏览器中，不会上传到服务器。后续我们将逐步完善更多实用功能。
              </p>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="font-heading text-2xl font-bold text-primary-600">
                  8+
                </p>
                <p className="mt-1 text-sm text-foreground-500">收录剧集</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="font-heading text-2xl font-bold text-primary-600">
                  8
                </p>
                <p className="mt-1 text-sm text-foreground-500">演员资料</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="font-heading text-2xl font-bold text-primary-600">
                  17+
                </p>
                <p className="mt-1 text-sm text-foreground-500">观众点评</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="font-heading text-lg font-bold text-foreground-950">
              服务范围
            </h3>
            <ul className="mt-3 space-y-3 text-sm text-foreground-600">
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-primary-600" />
                剧集剧情梗概与分集解析
              </li>
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-primary-600" />
                演员资料、代表作品与角色介绍
              </li>
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-primary-600" />
                剧集分类、筛选与关键词搜索
              </li>
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-primary-600" />
                观众评分与剧集点评汇总
              </li>
            </ul>
            <Link
              to="/contact"
              className="mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-customer-service-2-line" />
              联系我们
            </Link>
          </div>
        </section>

        {/* 团队简介 */}
        <section className="mt-14">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">
            团队简介
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            一支热爱影视内容的小团队，各司其职，把每一处细节做好。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {teamMembers.map((member) => (
              <div
                key={member.name}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${member.icon} text-xl`} />
                </span>
                <h3 className="text-base font-semibold text-foreground-900">
                  {member.name}
                </h3>
                <p className="mt-0.5 text-xs font-medium text-accent-800">
                  {member.role}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                  {member.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 隐私政策与免责声明 */}
        <section className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-shield-check-line text-primary-600" />
              隐私政策
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              本站尊重并保护用户隐私。你在点评与收藏功能中产生的数据，仅通过浏览器本地存储保存于你的设备，我们不会收集、上传或用于其他用途。清除浏览器数据即可删除这些内容。若你通过联系表单与我们取得联系，我们仅在回复咨询的范围内使用你提交的信息。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-file-warning-line text-primary-600" />
              免责声明
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              本站为影视内容介绍与解析平台，站内剧情、人物与图片均为示意展示，部分内容为虚构或整理自公开信息，仅供学习交流与观剧参考。站内不提供任何影视资源的播放或下载服务。如相关内容涉及版权问题，请通过联系页面与我们取得联系，我们将及时处理。
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}